import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {{price: number, compareAt: number|null}} SiblingPrice
 */

/**
 * Resolved prices for sibling products, keyed by handle.
 *
 * The metafield does not carry prices and `all_products` caps at 20 handles per
 * page, so they are fetched. The cache is shared across every picker instance
 * and mirrored into sessionStorage, because clicking a swatch is the point of
 * this component: a shopper walking a family of 48 designs pays for each handle
 * once per tab, not once per page view.
 *
 * A `null` entry means "asked, nothing there" — an unpublished sibling — and is
 * cached too, so it is not asked again.
 *
 * @type {Map<string, SiblingPrice|null>}
 */
const priceCache = new Map();

/**
 * Fetches in flight, so two tiles sharing a handle make one request.
 * @type {Map<string, Promise<SiblingPrice|null>>}
 */
const pending = new Map();

const STORAGE_PREFIX = 'jbg-xlink-price:';

/**
 * @param {string} handle
 * @returns {SiblingPrice|null|undefined} undefined when nothing is stored
 */
function readStoredPrice(handle) {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + handle);
    return raw == null ? undefined : JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/**
 * @param {string} handle
 * @param {SiblingPrice|null} value
 */
function storePrice(handle, value) {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + handle, JSON.stringify(value));
  } catch {
    // Private mode, or the quota is full. The in-memory cache still holds.
  }
}

/**
 * @param {string} root - routes.root_url, emptied when it is '/'
 * @param {string} handle
 * @returns {Promise<SiblingPrice|null>}
 */
async function fetchPrice(root, handle) {
  /** @type {SiblingPrice|null} */
  let value = null;

  try {
    const response = await fetch(`${root}/products/${encodeURIComponent(handle)}.js`, {
      priority: 'low',
    });
    if (response.ok) {
      const product = await response.json();
      value = {
        price: product.price,
        compareAt: product.compare_at_price > product.price ? product.compare_at_price : null,
      };
    }
  } catch {
    // Offline, or the sibling is gone. Cached as a miss either way.
  }

  priceCache.set(handle, value);
  storePrice(handle, value);
  pending.delete(handle);

  return value;
}

/**
 * Amazon's variation picker, paginated in place.
 *
 * Every swatch is already in the server HTML — the off-page ones carry `hidden`,
 * which also stops their lazy images being fetched — so paging is a visibility
 * toggle, not a refetch. That keeps the internal link graph crawlable.
 */
class JbgCrossLink extends Component {
  connectedCallback() {
    super.connectedCallback();

    for (const axis of this.#axes) this.#sync(axis);

    requestIdleCallback(() => {
      for (const axis of this.#axes) this.#loadPrices(axis);
    });
  }

  /** @param {{axis: number, page: number}} params */
  goToPage({ axis, page }) {
    const element = this.#axis(axis);
    if (!element) return;

    const pages = Number(element.dataset.pages) || 1;
    const target = Math.min(Math.max(page, 1), pages);
    if (target === Number(element.dataset.currentPage)) return;

    element.dataset.currentPage = String(target);
    this.#sync(element, true);
    this.#loadPrices(element);
  }

  /** @param {{axis: number, step: number}} params */
  stepPage({ axis, step }) {
    const element = this.#axis(axis);
    if (!element) return;
    this.goToPage({ axis, page: Number(element.dataset.currentPage) + step });
  }

  get #axes() {
    return Array.from(this.querySelectorAll('.jbg-xlink__axis'));
  }

  /** @param {number} index */
  #axis(index) {
    return this.querySelector(`.jbg-xlink__axis[data-axis="${index}"]`);
  }

  /**
   * @param {Element} element
   * @param {boolean} [announce]
   */
  #sync(element, announce = false) {
    const page = Number(element.dataset.currentPage) || 1;
    const pages = Number(element.dataset.pages) || 1;

    for (const swatch of element.querySelectorAll('li[data-page]')) {
      swatch.hidden = Number(swatch.dataset.page) !== page;
    }

    for (const button of element.querySelectorAll('[data-page-button]')) {
      if (Number(button.dataset.pageButton) === page) button.setAttribute('aria-current', 'page');
      else button.removeAttribute('aria-current');
    }

    for (const arrow of element.querySelectorAll('[data-page-step]')) {
      const next = page + Number(arrow.dataset.pageStep);
      arrow.disabled = next < 1 || next > pages;
    }

    const status = element.querySelector('[data-page-status]');
    if (status && announce) status.textContent = `${page} / ${pages}`;
  }

  /**
   * Nothing is fetched for a page the shopper never opens.
   *
   * @param {Element} element
   */
  #loadPrices(element) {
    if (!this.hasAttribute('data-show-price')) return;

    const slots = element.querySelectorAll('li:not([hidden]) [data-price-handle]:not([data-price-loaded])');

    for (const slot of slots) {
      const handle = slot.dataset.priceHandle;
      if (!handle) continue;

      if (!priceCache.has(handle)) {
        const stored = readStoredPrice(handle);
        if (stored !== undefined) priceCache.set(handle, stored);
      }

      if (priceCache.has(handle)) {
        this.#render(slot, priceCache.get(handle) ?? null);
        continue;
      }

      let request = pending.get(handle);
      if (!request) {
        request = fetchPrice(this.dataset.root ?? '', handle);
        pending.set(handle, request);
      }
      request.then((value) => this.#render(slot, value));
    }
  }

  /**
   * @param {Element} slot
   * @param {SiblingPrice|null} value
   */
  #render(slot, value) {
    slot.setAttribute('data-price-loaded', '');
    slot.textContent = '';

    // An unpublished sibling has no price to show. The slot keeps its reserved
    // height so the grid does not reflow.
    if (!value) return;

    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    slot.textContent = formatMoney(value.price, format, currency);

    if (value.compareAt) {
      const was = document.createElement('s');
      was.className = 'jbg-xlink__was';
      was.textContent = formatMoney(value.compareAt, format, currency);
      slot.append(was);
    }
  }
}

if (!customElements.get('jbg-cross-link')) {
  customElements.define('jbg-cross-link', JbgCrossLink);
}
